<script setup>
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MediaAssetsAPI from 'dashboard/api/mediaAssets';
import { useAlert } from 'dashboard/composables';
import { imagePx } from '../canvasStyles';
import ResizeHandles from '../ResizeHandles.vue';

const props = defineProps({
  element: { type: Object, required: true },
  inner: { type: Number, required: true },
  desktopInner: { type: Number, default: null },
  selected: { type: Boolean, default: false },
});
defineOptions({ inheritAttrs: false });
// Imagem, logo e capa do vídeo: largura real (px) igual ao e-mail, alças quando
// selecionada, soltar arquivo em cima troca a imagem (não vale para o vídeo,
// cuja capa tem o ▶).

const ed = inject('emailEditor');
const { t } = useI18n();
const livePx = ref(null);
const dropping = ref(false);
const uploading = ref(false);

const url = computed(
  () =>
    props.element.media?.url ||
    (props.element.type === 'logo'
      ? ed.template.value?.settings?.logo_url
      : null)
);
const cap = computed(() => props.desktopInner ?? props.inner);
const px = computed(
  () => livePx.value || Math.min(props.inner, imagePx(props.element, cap.value))
);
const hasFiles = event => event.dataTransfer?.types?.includes('Files');
const onDragOver = event => {
  if (props.element.type === 'video') return;
  if (!hasFiles(event)) return;
  event.preventDefault();
  dropping.value = true;
};
const JUSTIFY = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};
const st = computed(() => props.element.style || {});
const emptyHint = computed(() => {
  if (props.element.type === 'video') {
    return t('EMAIL_TEMPLATES.EDITOR.CANVAS.VIDEO_HINT');
  }
  return props.element.type === 'logo'
    ? t('EMAIL_TEMPLATES.EDITOR.PROPS.LOGO_HINT')
    : t('EMAIL_TEMPLATES.EDITOR.CANVAS.NO_IMAGE');
});
const overlayText = computed(() =>
  uploading.value
    ? t('EMAIL_TEMPLATES.EDITOR.CANVAS.UPLOADING')
    : t('EMAIL_TEMPLATES.EDITOR.CANVAS.DROP_IMAGE')
);

const commit = value => {
  livePx.value = null;
  ed.update(props.element.id, { width: { value, unit: 'px' } });
};

const onDrop = async event => {
  if (props.element.type === 'video') return;
  dropping.value = false;
  if (!hasFiles(event)) return;
  event.preventDefault();
  event.stopPropagation();
  const file = event.dataTransfer?.files?.[0];
  if (!file || !file.type.startsWith('image/')) return;
  uploading.value = true;
  try {
    const { data } = await MediaAssetsAPI.upload(file);
    ed.update(props.element.id, {
      media: { asset_id: data.id, url: data.url },
    });
  } catch (e) {
    useAlert(t('EMAIL_TEMPLATES.EDITOR.CANVAS.UPLOAD_ERROR'));
  } finally {
    uploading.value = false;
  }
};
</script>

<template>
  <div
    class="flex"
    :class="JUSTIFY[element.align] || 'justify-center'"
    @dragover="onDragOver"
    @dragleave="dropping = false"
    @drop="onDrop"
  >
    <div class="relative max-w-full" :style="{ width: `${px}px` }">
      <img
        v-if="url"
        :src="url"
        :alt="element.alt"
        class="block h-auto w-full select-none"
        draggable="false"
        :style="{
          borderRadius: st.radius ? `${st.radius}px` : undefined,
          border: st.border?.width
            ? `${st.border.width}px solid ${st.border.color}`
            : undefined,
        }"
      />
      <div
        v-else
        class="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-n-weak bg-n-solid-2 text-xs text-n-slate-10"
      >
        <span
          :class="
            element.type === 'video'
              ? 'i-lucide-circle-play size-8 text-n-slate-11'
              : 'i-lucide-image-plus size-6'
          "
        />
        {{ emptyHint }}
      </div>
      <div
        v-if="dropping || uploading"
        class="absolute inset-0 flex items-center justify-center rounded-md bg-n-brand/20 text-xs font-medium text-n-brand"
      >
        {{ overlayText }}
      </div>
      <ResizeHandles
        v-if="selected && url"
        :px="px"
        :max="cap"
        :align="element.align"
        @resize="v => (livePx = v)"
        @commit="commit"
      />
    </div>
  </div>
</template>
